<script setup>
import { computed, ref } from "vue";
import dayjs from "dayjs";
import apiClient from "@/apiClient";
import ReferenceService from "@/services/ReferenceService";
import SearchableInput from "@/components/SearchableInput.vue";
import { normalizeResourceAllocationGroups } from "@/utils/resourceAllocations";
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  MapPinIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/vue/24/solid";

const props = defineProps({
  job: { type: Object, required: true },
  vehicles: { type: Array, default: () => [] },
  vehiclesLoading: { type: Boolean, default: false },
  vehiclesError: { type: String, default: "" },
  canOverrideVehicleLimit: { type: Boolean, default: false },
});

const emit = defineEmits(["changed"]);
const expanded = ref(false);
const loadingDetail = ref(false);
const detail = ref(null);
const detailError = ref("");
const actionError = ref("");
const busyActions = ref({});
const pendingVehicleIds = ref({});
const personalVehicleDrafts = ref({});

const primaryDate = computed(() => props.job.primary_date);
const vehicleAllocations = computed(
  () => detail.value?.vehicle_allocations ?? [],
);
const quotedVehicleMaximum = computed(() => {
  const maximum = detail.value?.vehicle_allocation_limit?.quoted_max;
  return maximum === null || maximum === undefined || maximum === ""
    ? null
    : Math.max(0, Number(maximum));
});

const shiftPart = (value) => String(value ?? "");

const visibleShifts = computed(() => {
  const groups = new Map();
  (detail.value?.dates ?? []).forEach((date) => {
    const key = [
      date?.date,
      date?.startTime ?? date?.start_time,
      date?.finishTime ?? date?.finish_time,
      shiftPart(date?.location_name),
      shiftPart(date?.location_address),
    ].join("__");
    const existing = groups.get(key);
    if (existing) {
      existing.entries.push(date);
      existing.resources.push(...(date?.resources ?? []));
      return;
    }
    groups.set(key, {
      ...date,
      entries: [date],
      resources: [...(date?.resources ?? [])],
    });
  });
  return Array.from(groups.values());
});

const shiftDateIds = (date) =>
  new Set(
    (Array.isArray(date?.entries) ? date.entries : [date])
      .map((entry) => Number(entry?.id))
      .filter(Boolean),
  );

const driverOptionsForDate = (date) => {
  const staffById = new Map();
  (date?.resources ?? []).forEach((resource) => {
    (resource.personnel_assignments ?? []).forEach((assignment) => {
      const staff = assignment?.staff;
      const isDriver = (staff?.skills ?? []).some((skill) =>
        (skill?.name ?? "").toLowerCase().includes("driver"),
      );
      if (staff?.id && isDriver) {
        staffById.set(staff.id, staff);
      }
    });
  });
  return Array.from(staffById.values()).sort((a, b) =>
    (a?.contact?.name ?? "").localeCompare(b?.contact?.name ?? ""),
  );
};

const setBusy = (key, value) => {
  busyActions.value = { ...busyActions.value, [key]: value };
};

const isBusy = (key) => Boolean(busyActions.value[key]);

const getApiError = (error, fallback) => {
  const validation = error?.response?.data?.errors;
  return (
    error?.response?.data?.message ??
    (validation ? Object.values(validation).flat().find(Boolean) : null) ??
    fallback
  );
};

const loadDetail = async (force = false) => {
  if ((detail.value && !force) || loadingDetail.value) {
    return;
  }
  loadingDetail.value = true;
  detailError.value = "";
  try {
    const { data } = await apiClient.get(`/api/job/${props.job.id}`, {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    (data?.dates ?? []).forEach((date) => {
      normalizeResourceAllocationGroups(date?.resources ?? []);
    });
    detail.value = data;
  } catch (error) {
    console.error(error);
    detailError.value = getApiError(
      error,
      "Unable to load allocation details.",
    );
  } finally {
    loadingDetail.value = false;
  }
};

const toggle = async () => {
  expanded.value = !expanded.value;
  if (expanded.value) {
    await loadDetail();
  }
};

const formatPrimaryDate = () => {
  if (!primaryDate.value?.date) {
    return "Unscheduled";
  }
  const date = dayjs(primaryDate.value.date);
  if (date.isSame(dayjs(), "day")) {
    return "Today";
  }
  if (date.isSame(dayjs().add(1, "day"), "day")) {
    return "Tomorrow";
  }
  return date.format("ddd D MMM");
};

const formatDate = (date) =>
  date ? dayjs(date).format("ddd D MMM YYYY") : "Date TBD";

const normaliseTime = (value) => (value ? String(value).slice(0, 5) : null);

const formatTimeRange = (date) => {
  const start = normaliseTime(date?.startTime ?? date?.start_time);
  const finish = normaliseTime(date?.finishTime ?? date?.finish_time);
  return start && finish ? `${start}–${finish}` : "Time TBD";
};

const dateClass = computed(() => {
  if (!primaryDate.value?.date) {
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200";
  }
  if (props.job.scope === "past") {
    return "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200";
  }
  return dayjs(primaryDate.value.date).isSame(dayjs(), "day")
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
    : "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200";
});

const resourcingClass = computed(() =>
  props.job.resourcing.is_resourced
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
    : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
);

const resourcingLabel = computed(() => {
  const summary = props.job.resourcing;
  if (summary.is_resourced) {
    return "Resourced";
  }
  const missing = [];
  if (summary.vehicle_assigned < summary.vehicle_required) {
    missing.push(
      `vehicle ${summary.vehicle_assigned}/${summary.vehicle_required}`,
    );
  }
  if (summary.staff_assigned < summary.staff_required) {
    missing.push(`staff ${summary.staff_assigned}/${summary.staff_required}`);
  }
  return missing.length ? `Needs ${missing.join(" · ")}` : "Needs resources";
});

const copySummary = async () => {
  const date = primaryDate.value?.date
    ? formatDate(primaryDate.value.date)
    : "Date TBD";
  const text = `${date}\n\n${props.job.name} ${formatTimeRange(primaryDate.value)}`;
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error(error);
    actionError.value = "Unable to copy the job summary.";
  }
};

const getVehicleLabel = (vehicle) => {
  const registration = vehicle?.registrationNumber ?? `Vehicle #${vehicle?.id}`;
  const details = [vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
  return details ? `${registration} · ${details}` : registration;
};

const isPersonalVehicleAllocation = (allocation) =>
  allocation?.vehicle_source === "personal";

const vehicleAllocationsForDate = (date) =>
  vehicleAllocations.value.filter((allocation) =>
    shiftDateIds(date).has(Number(allocation.job_date_id)),
  );

const isVehicleOverrideApproved = (date) =>
  (Array.isArray(date?.entries) ? date.entries : [date]).some(
    (entry) => Boolean(entry?.vehicle_overallocation_approved_at),
  );

const canAddVehicleForDate = (date) =>
  quotedVehicleMaximum.value === null ||
  isVehicleOverrideApproved(date) ||
  vehicleAllocationsForDate(date).length < quotedVehicleMaximum.value;

const vehicleLimitMessage = (date) => {
  if (canAddVehicleForDate(date) || quotedVehicleMaximum.value === null) return "";
  const noun = quotedVehicleMaximum.value === 1 ? "vehicle" : "vehicles";
  return props.canOverrideVehicleLimit
    ? `The accepted quote allows ${quotedVehicleMaximum.value} ${noun}. Enable “Allow vehicles above quote” to add more.`
    : `The accepted quote allows ${quotedVehicleMaximum.value} ${noun}. Ask a manager with Super User access to approve more vehicles.`;
};

const updateVehicleLimitOverride = async (date, approved) => {
  if (!date?.id || !props.canOverrideVehicleLimit) return;
  const key = `override-${date.id}`;
  if (isBusy(key)) return;
  setBusy(key, true);
  actionError.value = "";
  try {
    const { data } = await apiClient.put(
      "/api/vehicle-allocation/override",
      { job_date_id: Number(date.id), approved: Boolean(approved) },
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    const affectedDateIds = new Set((data?.job_date_ids ?? []).map(Number));
    (detail.value?.dates ?? []).forEach((entry) => {
      if (!affectedDateIds.size || affectedDateIds.has(Number(entry.id))) {
        entry.vehicle_overallocation_approved_at =
          data?.override_approved_at ?? null;
        entry.vehicle_overallocation_approved_by =
          data?.override_approved_by ?? null;
      }
    });
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(
      error,
      "Unable to update the extra-vehicle approval.",
    );
  } finally {
    setBusy(key, false);
  }
};

const vehicleIsUsed = (date, allocation, vehicleId) =>
  vehicleAllocationsForDate(date).some(
    (candidate) =>
      candidate.id !== allocation.id &&
      Number(candidate.vehicle_id) === Number(vehicleId),
  );

const addableVehiclesForDate = (date) => {
  const used = new Set(
    vehicleAllocationsForDate(date).map((allocation) =>
      Number(allocation.vehicle_id),
    ),
  );
  return props.vehicles.filter((vehicle) => !used.has(Number(vehicle.id)));
};

const addVehiclePlaceholder = (date) => {
  if (props.vehiclesLoading) return "Loading active vehicles…";
  if (props.vehiclesError) return "Vehicles unavailable — retry above";
  if (props.vehicles.length === 0) return "No active vehicles available";
  return addableVehiclesForDate(date).length
    ? "+ Add vehicle to this shift"
    : "All active vehicles assigned";
};

const driverOptionsFor = (date, allocation) => {
  const options = [...driverOptionsForDate(date)];
  if (
    allocation?.driver?.id &&
    !options.some((staff) => staff.id === allocation.driver.id)
  ) {
    options.push(allocation.driver);
  }
  return options.sort((a, b) =>
    (a?.contact?.name ?? "").localeCompare(b?.contact?.name ?? ""),
  );
};

const driverIsUsed = (date, allocation, driverId) =>
  vehicleAllocationsForDate(date).some(
    (candidate) =>
      Number(candidate.id) !== Number(allocation?.id) &&
      Number(candidate.driver_staff_id) === Number(driverId),
  );

const mergeAllocation = (allocation) => {
  const allocations = vehicleAllocations.value;
  const index = allocations.findIndex(
    (candidate) => Number(candidate.id) === Number(allocation.id),
  );
  if (index >= 0) {
    allocations[index] = allocation;
  } else {
    allocations.push(allocation);
  }
};

const pendingVehicleIdFor = (date) => pendingVehicleIds.value[date?.id] ?? "";

const addVehicle = async (date, vehicleId) => {
  pendingVehicleIds.value = {
    ...pendingVehicleIds.value,
    [date.id]: vehicleId,
  };
  if (!vehicleId || !canAddVehicleForDate(date)) return;
  const key = `add-vehicle-${date.id}`;
  setBusy(key, true);
  actionError.value = "";
  try {
    const { data } = await apiClient.post(
      "/api/vehicle-allocation",
      {
        reference_id: props.job.id,
        job_date_id: Number(date.id),
        vehicle_id: Number(vehicleId),
      },
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    mergeAllocation(data);
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(error, "Unable to allocate the vehicle.");
  } finally {
    pendingVehicleIds.value = {
      ...pendingVehicleIds.value,
      [date.id]: "",
    };
    setBusy(key, false);
  }
};

const updateAllocation = async (allocation, payload) => {
  const key = `allocation-${allocation.id}`;
  if (isBusy(key)) return;
  setBusy(key, true);
  actionError.value = "";
  try {
    const { data } = await apiClient.put(
      `/api/vehicle-allocation/${allocation.id}`,
      payload,
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    mergeAllocation(data);
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(error, "Unable to update the allocation.");
  } finally {
    setBusy(key, false);
  }
};

const changeVehicle = (allocation, vehicleId) => {
  if (!vehicleId || Number(vehicleId) === Number(allocation.vehicle_id)) return;
  return updateAllocation(allocation, {
    job_date_id: Number(allocation.job_date_id),
    vehicle_id: Number(vehicleId),
    driver_staff_id: allocation.driver_staff_id ?? null,
  });
};

const changeDriver = (allocation, staffId) =>
  updateAllocation(allocation, {
    job_date_id: Number(allocation.job_date_id),
    vehicle_source: allocation.vehicle_source ?? "fleet",
    vehicle_id: allocation.vehicle_id ? Number(allocation.vehicle_id) : null,
    personal_vehicle_registration:
      allocation.personal_vehicle_registration ?? null,
    driver_staff_id: staffId ? Number(staffId) : null,
  });

const changePersonalVehicleRegistration = (allocation, registration) => {
  const value = String(registration ?? "").trim();
  if (!value || value === allocation.personal_vehicle_registration) return;
  return updateAllocation(allocation, {
    job_date_id: Number(allocation.job_date_id),
    vehicle_source: "personal",
    vehicle_id: null,
    personal_vehicle_registration: value,
    driver_staff_id: allocation.driver_staff_id ?? null,
  });
};

const personalVehicleDraftFor = (date) =>
  personalVehicleDrafts.value[date?.id] ?? null;

const startPersonalVehicle = (date) => {
  if (!canAddVehicleForDate(date)) return;
  personalVehicleDrafts.value = {
    ...personalVehicleDrafts.value,
    [date.id]: { registration: "", driver_staff_id: "" },
  };
};

const cancelPersonalVehicle = (date) => {
  const next = { ...personalVehicleDrafts.value };
  delete next[date.id];
  personalVehicleDrafts.value = next;
};

const updatePersonalVehicleDraft = (date, field, value) => {
  personalVehicleDrafts.value = {
    ...personalVehicleDrafts.value,
    [date.id]: {
      ...personalVehicleDraftFor(date),
      [field]: value,
    },
  };
};

const addPersonalVehicle = async (date) => {
  const draft = personalVehicleDraftFor(date);
  if (!draft?.registration || !draft?.driver_staff_id || !canAddVehicleForDate(date)) return;
  const key = `add-personal-vehicle-${date.id}`;
  setBusy(key, true);
  actionError.value = "";
  try {
    const { data } = await apiClient.post(
      "/api/vehicle-allocation",
      {
        reference_id: props.job.id,
        job_date_id: Number(date.id),
        vehicle_source: "personal",
        vehicle_id: null,
        personal_vehicle_registration: draft.registration,
        driver_staff_id: Number(draft.driver_staff_id),
      },
      { suppressAutoReload: true, suppressErrorAlert: true },
    );
    mergeAllocation(data);
    cancelPersonalVehicle(date);
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(
      error,
      "Unable to allocate the staff vehicle.",
    );
  } finally {
    setBusy(key, false);
  }
};

const removeVehicle = async (allocation) => {
  const key = `allocation-${allocation.id}`;
  setBusy(key, true);
  actionError.value = "";
  try {
    await apiClient.delete(`/api/vehicle-allocation/${allocation.id}`, {
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    detail.value.vehicle_allocations = vehicleAllocations.value.filter(
      (candidate) => Number(candidate.id) !== Number(allocation.id),
    );
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(error, "Unable to remove the vehicle.");
  } finally {
    setBusy(key, false);
  }
};

const requirementsFor = (resource) =>
  resource?.product?.personnel_requirements ?? [];

const assignmentsFor = (resource, requirementId) =>
  (resource?.personnel_assignments ?? []).filter(
    (assignment) =>
      Number(assignment.product_personnel_requirement_id) ===
      Number(requirementId),
  );

const requiredSlotsFor = (resource, requirement) =>
  Math.max(1, Number(resource?.quantity) || 1) *
  Math.max(1, Number(requirement?.required_count) || 1);

const personnelSlotsFor = (resource, requirement) => {
  const assignments = assignmentsFor(resource, requirement.id);
  const slotCount = Math.max(
    assignments.length,
    requiredSlotsFor(resource, requirement),
  );

  return Array.from(
    { length: slotCount },
    (_, index) => assignments[index] ?? null,
  );
};

const assignStaff = async (resource, requirement, assignment, staff) => {
  const key = `staff-${resource.id}-${requirement.id}`;
  setBusy(key, true);
  actionError.value = "";
  try {
    await new ReferenceService(props.job.id).allocateStaff(
      resource.id,
      requirement.id,
      staff.id,
      {
        assignmentId: assignment?.id ?? null,
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    await loadDetail(true);
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(
      error,
      "Unable to assign the staff member.",
    );
  } finally {
    setBusy(key, false);
  }
};

const unassignStaff = async (resource, requirement, assignment) => {
  const key = `staff-${resource.id}-${requirement.id}`;
  setBusy(key, true);
  actionError.value = "";
  try {
    await new ReferenceService(props.job.id).allocateStaff(
      assignment.job_resource_id ?? resource.id,
      requirement.id,
      null,
      {
        assignmentId: assignment.id,
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    await loadDetail(true);
    emit("changed");
  } catch (error) {
    console.error(error);
    actionError.value = getApiError(
      error,
      "Unable to unassign the staff member.",
    );
  } finally {
    setBusy(key, false);
  }
};
</script>

<template>
  <article
    class="overflow-visible rounded-xl border bg-white shadow-sm transition dark:bg-slate-800"
    :class="
      job.resourcing.is_resourced
        ? 'border-l-4 border-slate-200 border-l-emerald-400 dark:border-slate-700 dark:border-l-emerald-500'
        : 'border-l-4 border-slate-200 border-l-amber-400 dark:border-slate-700 dark:border-l-amber-500'
    "
  >
    <div
      class="grid items-center gap-3 p-4 md:grid-cols-[120px_minmax(220px,1.4fr)_minmax(180px,1fr)_auto_auto]"
    >
      <div>
        <div class="text-sm font-semibold text-slate-900 dark:text-white">
          {{ formatPrimaryDate() }}
        </div>
        <div class="mt-0.5 text-xs text-slate-500">
          {{ formatTimeRange(primaryDate) }}
        </div>
      </div>

      <div class="min-w-0">
        <div class="flex min-w-0 items-center gap-2">
          <router-link
            :to="`/reference/${job.id}`"
            class="truncate font-semibold text-slate-900 hover:underline dark:text-white"
          >
            {{ job.name }}
          </router-link>
          <span class="shrink-0 text-xs text-slate-500">{{ job.txt }}</span>
        </div>
        <div class="mt-1 text-xs text-slate-500">
          {{ job.type?.name ?? "Unknown type" }}
        </div>
      </div>

      <div
        class="flex min-w-0 items-start gap-2 text-sm text-slate-600 dark:text-slate-300"
      >
        <MapPinIcon class="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span class="truncate" :title="job.location">{{ job.location }}</span>
      </div>

      <div
        class="flex flex-wrap items-center justify-start gap-2 md:justify-end"
      >
        <span
          class="rounded-full px-2 py-1 text-xs font-medium"
          :class="dateClass"
        >
          {{
            job.scope === "past"
              ? "Previous"
              : primaryDate
                ? "Scheduled"
                : "Unscheduled"
          }}
        </span>
        <span
          class="rounded-full px-2 py-1 text-xs font-medium"
          :class="resourcingClass"
        >
          {{ resourcingLabel }}
        </span>
      </div>

      <div class="flex justify-end gap-1">
        <button
          type="button"
          class="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-700 dark:hover:text-white"
          title="Copy job summary"
          aria-label="Copy job summary"
          @click="copySummary"
        >
          <ClipboardDocumentIcon class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          :aria-expanded="expanded"
          @click="toggle"
        >
          Manage
          <ChevronDownIcon
            class="h-4 w-4 transition"
            :class="expanded ? 'rotate-180' : ''"
          />
        </button>
      </div>
    </div>

    <div
      v-if="expanded"
      class="border-t border-slate-200 p-4 dark:border-slate-700"
    >
      <div v-if="loadingDetail && !detail" class="animate-pulse space-y-3">
        <div class="h-4 w-40 rounded bg-slate-200 dark:bg-slate-700"></div>
        <div class="h-20 rounded bg-slate-100 dark:bg-slate-700/60"></div>
      </div>

      <div
        v-else-if="detailError && !detail"
        class="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200"
      >
        {{ detailError }}
        <button
          type="button"
          class="ml-2 font-semibold underline"
          @click="loadDetail(true)"
        >
          Retry
        </button>
      </div>

      <div
        v-else-if="detail"
        class="space-y-4"
      >
        <section>
          <h3
            class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300"
          >
            Staff, vehicles &amp; drivers by shift
          </h3>
          <div v-if="visibleShifts.length" class="space-y-3">
            <div
              v-for="date in visibleShifts"
              :key="date.id"
              data-testid="job-list-resource-date"
              class="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="font-semibold text-slate-900 dark:text-white">
                  {{ formatDate(date.date) }}
                </div>
                <div class="text-xs text-slate-500">
                  {{ formatTimeRange(date) }}
                </div>
              </div>
              <div
                v-if="date.location_name || date.location_address"
                class="mt-1 flex items-start gap-1 text-xs text-slate-500"
              >
                <MapPinIcon class="h-3 w-3 shrink-0" />
                <span>{{
                  [date.location_name, date.location_address]
                    .filter(Boolean)
                    .join(" · ")
                }}</span>
              </div>

              <div v-if="date.resources?.length" class="mt-3 space-y-2">
                <div
                  v-for="resource in date.resources"
                  :key="resource.id"
                  class="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40"
                >
                  <div
                    class="text-xs font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {{
                      resource.product?.short ??
                      resource.product?.name ??
                      "Resource"
                    }}
                  </div>
                  <div
                    v-if="requirementsFor(resource).length"
                    class="mt-2 grid gap-2 lg:grid-cols-2"
                  >
                    <div
                      v-for="requirement in requirementsFor(resource)"
                      :key="requirement.id"
                      class="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800"
                    >
                      <div
                        class="flex items-center justify-between text-xs text-slate-500"
                      >
                        <span>{{ requirement.skill?.name ?? "Skill" }}</span>
                        <span
                          >{{
                            assignmentsFor(resource, requirement.id).length
                          }}/{{ requiredSlotsFor(resource, requirement) }}</span
                        >
                      </div>
                      <div class="mt-2 space-y-2">
                        <div
                          v-for="(assignment, slotIndex) in personnelSlotsFor(
                            resource,
                            requirement,
                          )"
                          :key="assignment?.id ?? `${requirement.id}-${slotIndex}`"
                        >
                          <div
                            v-if="assignment"
                            class="flex items-center justify-between gap-2 rounded bg-emerald-100 px-2 py-1.5 text-xs text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                          >
                            <router-link
                              :to="{
                                name: 'staff-view',
                                params: { id: assignment.staff_id },
                              }"
                              class="inline-flex min-w-0 items-center gap-1 hover:underline"
                            >
                              <UserCircleIcon class="h-3 w-3 shrink-0" />
                              <span class="truncate">
                                {{
                                  assignment.staff?.contact?.name ??
                                  `Staff #${assignment.staff_id}`
                                }}
                              </span>
                            </router-link>
                            <button
                              type="button"
                              class="shrink-0 rounded-full p-0.5 hover:bg-emerald-200 dark:hover:bg-emerald-800"
                              :disabled="
                                isBusy(`staff-${resource.id}-${requirement.id}`)
                              "
                              aria-label="Unassign staff member"
                              @click="
                                unassignStaff(
                                  resource,
                                  requirement,
                                  assignment,
                                )
                              "
                            >
                              <XMarkIcon class="h-3 w-3" />
                            </button>
                          </div>
                          <SearchableInput
                            v-else-if="requirement.staff_skill_id"
                            :url="`/api/staff/search?skill_id=${requirement.staff_skill_id}&query=`"
                            :output="(staff) => staff.contact.name"
                            :placeholder="`Assign ${requirement.skill?.name ?? 'staff'} ${slotIndex + 1}`"
                            @selected="
                              (staff) =>
                                assignStaff(
                                  resource,
                                  requirement,
                                  assignment,
                                  staff,
                                )
                            "
                          />
                          <div v-else class="text-xs text-slate-400">
                            Skill setup required.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="mt-2 text-xs text-slate-500">
                    No personnel requirements.
                  </div>
                </div>
              </div>
              <div v-else class="mt-2 text-xs text-slate-500">
                No resources scheduled.
              </div>

              <section
                data-testid="job-list-vehicle-date"
                class="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 p-3 dark:border-sky-900 dark:bg-sky-950/20"
              >
                <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wide text-sky-800 dark:text-sky-200">
                      Vehicles &amp; drivers
                    </h4>
                    <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      Choose drivers from the crew assigned above.
                    </p>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="rounded-full px-2 py-1 text-[11px] font-semibold"
                      :class="vehicleAllocationsForDate(date).length
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'"
                    >
                      {{ vehicleAllocationsForDate(date).length }}
                      <template v-if="quotedVehicleMaximum !== null"> / {{ quotedVehicleMaximum }} quoted</template>
                      vehicle{{ quotedVehicleMaximum === 1 ? '' : 's' }}
                    </span>
                    <label
                      v-if="canOverrideVehicleLimit && quotedVehicleMaximum !== null"
                      class="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
                    >
                      <input
                        type="checkbox"
                        data-testid="job-list-vehicle-limit-override"
                        :checked="isVehicleOverrideApproved(date)"
                        :disabled="isBusy(`override-${date.id}`)"
                        @change="updateVehicleLimitOverride(date, $event.target.checked)"
                      >
                      Allow vehicles above quote
                    </label>
                  </div>
                </div>

                <div
                  v-if="vehicleAllocationsForDate(date).length === 0"
                  class="mb-2 rounded border border-dashed border-slate-300 px-2 py-2 text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400"
                >
                  No vehicle assigned to this shift.
                </div>

                <div
                  v-for="allocation in vehicleAllocationsForDate(date)"
                  :key="allocation.id"
                  class="mb-2 grid gap-2 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900/50 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end"
                >
                  <div v-if="isPersonalVehicleAllocation(allocation)">
                    <span class="mb-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                      Own car
                    </span>
                    <input
                      :value="allocation.personal_vehicle_registration ?? ''"
                      data-testid="job-list-personal-vehicle-registration"
                      class="w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs uppercase text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      :disabled="isBusy(`allocation-${allocation.id}`)"
                      aria-label="Staff vehicle registration"
                      @change="changePersonalVehicleRegistration(allocation, $event.target.value)"
                    >
                  </div>
                  <select
                    v-else
                    :value="allocation.vehicle_id"
                    class="w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    :disabled="isBusy(`allocation-${allocation.id}`)"
                    @change="changeVehicle(allocation, $event.target.value)"
                  >
                    <option
                      v-if="allocation.vehicle && !vehicles.some((vehicle) => Number(vehicle.id) === Number(allocation.vehicle.id))"
                      :value="allocation.vehicle.id"
                    >
                      {{ getVehicleLabel(allocation.vehicle) }}
                    </option>
                    <option
                      v-for="vehicle in vehicles"
                      :key="vehicle.id"
                      :value="vehicle.id"
                      :disabled="vehicleIsUsed(date, allocation, vehicle.id)"
                    >
                      {{ getVehicleLabel(vehicle) }}
                    </option>
                  </select>

                  <select
                    :value="allocation.driver_staff_id ?? ''"
                    class="w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    :disabled="isBusy(`allocation-${allocation.id}`)"
                    @change="changeDriver(allocation, $event.target.value)"
                  >
                    <option value="" :disabled="isPersonalVehicleAllocation(allocation)">
                      {{ isPersonalVehicleAllocation(allocation) ? 'Select owner/driver' : 'No driver selected' }}
                    </option>
                    <option
                      v-for="driver in driverOptionsFor(date, allocation)"
                      :key="driver.id"
                      :value="driver.id"
                      :disabled="driverIsUsed(date, allocation, driver.id)"
                    >
                      {{ driver.contact?.name ?? `Staff #${driver.id}` }}
                    </option>
                  </select>

                  <button
                    type="button"
                    class="self-end rounded border border-red-300 px-3 py-2 text-xs font-semibold text-red-600 disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400 dark:border-red-800 dark:text-red-300"
                    :disabled="isBusy(`allocation-${allocation.id}`)"
                    title="Remove vehicle"
                    @click="removeVehicle(allocation)"
                  >
                    Remove
                  </button>
                </div>

                <select
                  :value="pendingVehicleIdFor(date)"
                  data-testid="job-list-add-vehicle"
                  class="w-full rounded border border-dashed border-sky-300 bg-white px-2 py-2 text-xs font-medium text-sky-800 dark:border-sky-700 dark:bg-slate-900 dark:text-sky-200"
                  :disabled="vehiclesLoading || Boolean(vehiclesError) || isBusy(`add-vehicle-${date.id}`) || addableVehiclesForDate(date).length === 0 || !canAddVehicleForDate(date)"
                  @change="addVehicle(date, $event.target.value)"
                >
                  <option value="">
                    {{ addVehiclePlaceholder(date) }}
                  </option>
                  <option
                    v-for="vehicle in addableVehiclesForDate(date)"
                    :key="vehicle.id"
                    :value="vehicle.id"
                  >
                    {{ getVehicleLabel(vehicle) }}
                  </option>
                </select>

                <div
                  v-if="personalVehicleDraftFor(date)"
                  data-testid="job-list-personal-vehicle-draft"
                  class="mt-2 grid gap-2 rounded border border-dashed border-violet-300 bg-violet-50 p-2 dark:border-violet-800 dark:bg-violet-950/20 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
                >
                  <input
                    :value="personalVehicleDraftFor(date).registration"
                    data-testid="job-list-new-personal-vehicle-registration"
                    class="w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs uppercase text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    placeholder="Registration"
                    aria-label="Staff vehicle registration"
                    @input="updatePersonalVehicleDraft(date, 'registration', $event.target.value)"
                  >
                  <select
                    :value="personalVehicleDraftFor(date).driver_staff_id"
                    data-testid="job-list-new-personal-vehicle-driver"
                    class="w-full rounded border border-slate-300 bg-white px-2 py-2 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    aria-label="Staff vehicle driver"
                    @change="updatePersonalVehicleDraft(date, 'driver_staff_id', $event.target.value)"
                  >
                    <option value="">Select owner/driver</option>
                    <option
                      v-for="driver in driverOptionsForDate(date)"
                      :key="`personal-driver-${date.id}-${driver.id}`"
                      :value="driver.id"
                      :disabled="driverIsUsed(date, null, driver.id)"
                    >
                      {{ driver.contact?.name ?? `Staff #${driver.id}` }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="rounded bg-violet-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    :disabled="!personalVehicleDraftFor(date).registration || !personalVehicleDraftFor(date).driver_staff_id || isBusy(`add-personal-vehicle-${date.id}`) || !canAddVehicleForDate(date)"
                    @click="addPersonalVehicle(date)"
                  >
                    {{ isBusy(`add-personal-vehicle-${date.id}`) ? 'Adding…' : 'Add own car' }}
                  </button>
                  <button
                    type="button"
                    class="rounded border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-300"
                    :disabled="isBusy(`add-personal-vehicle-${date.id}`)"
                    @click="cancelPersonalVehicle(date)"
                  >
                    Cancel
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  data-testid="job-list-add-personal-vehicle"
                  class="mt-2 w-full rounded border border-dashed border-violet-300 bg-white px-3 py-2 text-xs font-semibold text-violet-700 dark:border-violet-700 dark:bg-slate-900 dark:text-violet-300"
                  :disabled="!canAddVehicleForDate(date)"
                  @click="startPersonalVehicle(date)"
                >
                  + Add staff's own car
                </button>

                <p
                  v-if="vehicleLimitMessage(date)"
                  data-testid="job-list-vehicle-limit-message"
                  class="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                >
                  {{ vehicleLimitMessage(date) }}
                </p>
                <p
                  v-else-if="quotedVehicleMaximum !== null && isVehicleOverrideApproved(date)"
                  class="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300"
                >
                  A Super User has approved vehicles above the accepted quote for this shift.
                </p>

                <p
                  v-if="driverOptionsForDate(date).length === 0"
                  class="mt-2 text-xs text-amber-700 dark:text-amber-300"
                >
                  Assign a crew member with a driver skill above to select a driver.
                </p>
              </section>
            </div>
          </div>
          <div v-else class="text-sm text-slate-500">No dates scheduled.</div>

          <div
            v-if="vehicleAllocations.some((allocation) => !allocation.job_date_id)"
            class="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            This job still has an older job-wide vehicle assignment.
          </div>
        </section>
      </div>

      <div
        v-if="actionError"
        class="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200"
      >
        {{ actionError }}
      </div>
    </div>
  </article>
</template>
